/**
 * Authentication state.
 *
 * The source of truth is the server: on boot we try to refresh the httpOnly
 * cookie into an access token, then ask `auth.me`. Nothing about the user is
 * trusted from local storage, so revoking a session takes effect on reload
 * rather than whenever the client feels like noticing.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { API_URL, trpcClient } from "./trpc.js";
import { createKeyBundle, forgetKeys, restore, unlock } from "./keyring.js";

/**
 * Start the application over.
 *
 * A real navigation, not a router transition: the point is to discard
 * everything this page was holding.
 */
function reload(path: string): void {
  window.location.assign(path);
}
import {
  clearSession,
  onSessionExpired,
  refreshSession,
  setSession,
} from "./session.js";

export interface CurrentUser {
  id: string;
  username: string;
  displayName: string;
  avatarFileId: string | null;
  /** A small JPEG data URL, or null. Deliberately not encrypted — see the API. */
  avatarImage?: string | null;
  isPlatformAdmin: boolean;
  totpEnabled: boolean;
  email: string | null;
  emailVerified: boolean;
  /** The public half of this account’s keypair; null before one is made. */
  publicKey?: string | null;
}

interface AuthState {
  status: "loading" | "authenticated" | "anonymous";
  user: CurrentUser | null;
  signIn: (
    username: string,
    password: string,
    totpCode?: string,
  ) => Promise<void>;
  /*
   * Registration no longer joins a group, so it no longer returns one. An
   * account starts belonging to nothing, and the caller sends the person on to
   * join or to wait for an invitation.
   */
  register: (input: {
    code?: string;
    username: string;
    password: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    email: string;
    displayName?: string;
  }) => Promise<void>;
  /**
   * Reopen the keyring for somebody who is already signed in.
   *
   * The keyring lives in memory and in this device IndexedDB, and neither
   * survives every circumstance — a different browser, cleared site data, a
   * private window. What was left behind was a grid of padlocks, no explanation,
   * and one way out: sign out and sign in again, losing your place, to fix
   * something that has nothing to do with the session.
   *
   * This is that sign-in without the leaving. It is a real login under the
   * covers, deliberately: the sealed private key opens only with a key derived
   * from the password, so the password has to arrive, and `login` is already
   * the one endpoint that hands the sealed key back to somebody who has just
   * proved they know it. Adding a session-authenticated way to fetch it would
   * mean a stolen access token could take the key away and attack it offline.
   */
  unlockKeyring: (password: string, totpCode?: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [user, setUser] = useState<CurrentUser | null>(null);
  /*
   * The current user, readable from a callback that must not be re-created.
   *
   * Kept in step below. The alternative — closing over `user` — is what made
   * unlocking report a correct password as wrong: the callback was memoised on
   * the first render, when the session had not loaded and `user` was null.
   */
  const userRef = useRef<CurrentUser | null>(null);

  const loadUser = useCallback(async () => {
    try {
      const me = await trpcClient.auth.me.query();

      /*
       * Reopen the keyring before anything renders that would ask for a
       * photograph. This is what makes a refresh a refresh rather than a
       * lock-out — the key is held by this device, not rebuilt from a password
       * nobody is being asked for.
       *
       * Not fatal if it fails: the account works, and the interface shows
       * photographs as closed until the next sign-in.
       */
      await restore((me as CurrentUser).id).catch(() => false);

      setUser(me as CurrentUser);
      setStatus("authenticated");
    } catch {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  // Boot: attempt a silent refresh. A returning user with a live cookie lands
  // straight in their gallery; everyone else sees the sign-in screen.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const refreshed = await refreshSession(API_URL);
      if (cancelled) return;
      if (!refreshed) {
        setStatus("anonymous");
        return;
      }
      await loadUser();
    })();

    return () => {
      cancelled = true;
    };
  }, [loadUser]);

  // The session layer tells us when a refresh has definitively failed — for
  // example after the server detected token reuse and killed the family.
  useEffect(() => {
    onSessionExpired(() => {
      // The keyring goes with the session. Without a session the key opens
      // nothing anyway — signed URLs need one — but leaving it behind on a
      // device nobody is signed in on serves no purpose.
      void forgetKeys();
      setUser(null);
      setStatus("anonymous");
    });
  }, []);

  const signIn = useCallback<AuthState["signIn"]>(
    async (username, password, totpCode) => {
      const result = await trpcClient.auth.login.mutate({
        username,
        password,
        ...(totpCode ? { totpCode } : {}),
        deviceName: describeDevice(),
      });
      setSession(result.accessToken, result.expiresIn);

      /*
       * Open the keyring here or not at all. The password is in scope for the
       * length of this function and is deliberately kept nowhere else, so this
       * is the only place the sealed private key can be rebuilt.
       *
       * A failure is not fatal to signing in: the account works, and the
       * interface reports that photographs cannot be opened. Treating it as a
       * failed login would lock somebody out of settings and support over a
       * problem they can only fix from inside.
       */
      if (result.keys) {
        const opened = await unlock(result.keys, password, result.user.id);
        if (!opened) {
          console.warn("signed in, but the keyring could not be opened");
        }
      } else {
        /*
         * An account with no keypair yet: the first admin, which the server
         * creates at boot and cannot give one to, or anyone who registered
         * before encryption existed. One is made here and now, because the
         * password is in memory here and now and nowhere else.
         *
         * A failure is not fatal to signing in. The account works, photographs
         * do not, and the next sign-in tries again — which is better than
         * refusing to let somebody in over a step they cannot influence.
         */
        try {
          const keys = await createKeyBundle(password);
          await trpcClient.auth.setupKeys.mutate({ password, keys });
          await unlock(keys, password, result.user.id);
        } catch (error) {
          console.warn("signed in, but no keypair could be created", error);
        }
      }

      setUser(result.user as CurrentUser);
      setStatus("authenticated");

      /*
       * And in again from a clean slate, for the same reason.
       *
       * The keyring is already open at this point and stays open across the
       * reload — it is held by the device, not by this page — so what comes
       * back is a signed-in session with photographs visible, not a second
       * sign-in.
       */
      reload("/");
    },
    [],
  );

  const unlockKeyring = useCallback<AuthState["unlockKeyring"]>(
    async (password, totpCode) => {
      /*
       * Read through a ref, not through the closure.
       *
       * This was `useCallback(…, [])` reading `user` directly, which captured
       * the value from the first render — `null`, because the session is still
       * loading then — and kept it for ever. So every unlock returned false
       * before it had looked at anything, and the screen said the password was
       * wrong. It had never been tried.
       *
       * A ref rather than a dependency because this must not be re-created as
       * the user object is refreshed: it is handed to a form that may already
       * be mid-submit.
       */
      const current = userRef.current;
      if (!current) {
        throw new Error("You are not signed in on this browser any more.");
      }

      const result = await trpcClient.auth.login.mutate({
        username: current.username,
        password,
        ...(totpCode ? { totpCode } : {}),
        deviceName: describeDevice(),
      });

      // A fresh session comes with it, which is correct — this *is* a sign-in.
      // What it deliberately does not do is reload the page, which is the whole
      // reason somebody would rather do this than sign out.
      setSession(result.accessToken, result.expiresIn);

      if (!result.keys) {
        throw new Error(
          "This account has no encryption key yet. Sign out and in again.",
        );
      }

      return unlock(result.keys, password, result.user.id);
    },
    [],
  );

  const register = useCallback<AuthState["register"]>(async (input) => {
    /*
     * Generated in the browser, before the server hears anything. What travels
     * is a public key and a private one already sealed under the password —
     * the server cannot open either, now or later, which is the property the
     * whole scheme rests on.
     */
    const keys = await createKeyBundle(input.password);
    const result = await trpcClient.auth.register.mutate({ ...input, keys });
    await unlock(keys, input.password, result.user.id);
    setSession(result.accessToken, result.expiresIn);
    setUser({
      id: result.user.id,
      username: result.user.username,
      displayName: result.user.displayName,
      avatarFileId: null,
      isPlatformAdmin: false,
      totpEnabled: false,
      email: result.user.email,
      // Freshly registered, so certainly not yet — the link has only just been
      // sent. Assuming otherwise would hide the reminder from the one person
      // who most needs it.
      emailVerified: false,
    });
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(async () => {
    try {
      await trpcClient.auth.logout.mutate();
    } finally {
      /*
       * Awaited, unlike the rest of this.
       *
       * `lock` clears the stored key as well as the in-memory one, and that
       * clearing is a database transaction. Reloading the page before it lands
       * would leave the key behind on a machine somebody has just signed out
       * of — the one outcome this is not allowed to have.
       */
      await forgetKeys();

      // Clear locally even if the call failed — the user asked to leave, and
      // the refresh cookie has a server-side expiry regardless.
      clearSession();
      setUser(null);
      setStatus("anonymous");

      /*
       * Then start over from nothing.
       *
       * Signing out of a single-page application otherwise leaves every cache,
       * query result and decrypted thumbnail from the previous session sitting
       * in memory, cleared only as carefully as somebody remembered to clear
       * it. A reload is the one approach that does not depend on remembering.
       */
      reload("/");
    }
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      signIn,
      register,
      unlockKeyring,
      signOut,
      refreshUser: loadUser,
    }),
    [status, user, signIn, register, unlockKeyring, signOut, loadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}

/** A label for the "active sessions" list, not fingerprinting. */
function describeDevice(): string {
  const ua = navigator.userAgent;
  const platform = /iPhone|iPad/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Macintosh/.test(ua)
        ? "macOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Linux/.test(ua)
            ? "Linux"
            : "Unknown";

  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
      ? "Chrome"
      : /Safari\//.test(ua)
        ? "Safari"
        : /Firefox\//.test(ua)
          ? "Firefox"
          : "Browser";

  return `${browser} on ${platform}`;
}
